/* Copyright (c) Trimble Inc. All rights reserved. */

import { Button, ButtonProps, CircularProgress } from '@material-ui/core';
import { AsyncActionState } from 'legacy-components/form/action/AsyncAction';
import React, { useEffect, useMemo } from 'react';

export interface SubmitProps<Response> extends ButtonProps {
  state?: AsyncActionState<Response|null>;
  disabled?: boolean;
  testid?: string;
  onSuccess?: (value: Response) => void;
}

/**
 * @component Submit
 * @description this Button will be associated with immediate upper level Form tag and validates  the form
 * If form is valid, then this button is enabled.
 * @param disabled - for any custom validations,extending this option
 * @param state - once this button is clicked, and asynchronous action is in progress, the AsyncActionState
 * which passed will show loader
 * @returns Button customized for Form validations.
 */
export const Submit: React.FC<SubmitProps<Response>> = ({
  disabled = false,
  state,
  children,
  onSuccess,
  ...props
}) => {

  useEffect(() => {
    if(onSuccess && state?.value) {
      onSuccess(state?.value);
    }
  },[onSuccess, state?.value])

  return useMemo(
    () => (
      <Button
        {...props}
        disabled={
          disabled ||
          !!state?.loading 
        }
        data-testid={props['testid'] ?? 'submit'}
        startIcon={
          state?.loading ? (
            <CircularProgress
              color="inherit"
              style={{ color: 'inherit' }}
              size="1rem"
              thickness={7}
            />
          ) : (
            props.startIcon
          )
        }
      >
        {children}
      </Button>
    ),
    [
      children,
      disabled,
      props,
      state?.loading,
    ]
  );
};

export default Submit;
